import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { homeFor, useSession } from '@/lib/session';
import { Field, Loading, Note, PasswordInput } from '@/components/ui';
import { Brandmark } from '@/components/common/Brandmark';
import { AuthHologram } from './AuthHologram';

/* El navy exacto del degradado de `.auth-stage` (no el `--navy` de marca,
   un poco más claro): Safari tiñe la barra de direcciones y, en algunas
   versiones, el área del reloj con este color cuando coincide con lo que hay
   pintado en los bordes de la página — si no coincide, se queda con su gris
   de siempre. Mientras el login está montado, tanto el body como el
   `theme-color` pasan a este tono; se revierte al desmontar. Esto no
   funciona en todas las versiones de Safari ni si la persona tiene apagada
   la opción de "usar el color del sitio" — lo que sí funciona siempre es
   instalar Clean Blue desde "Agregar a inicio": ahí no hay barras de Safari
   que teñir, ya está configurado `black-translucent` para eso. */
const AUTH_CHROME_COLOR = '#101526';

function useAuthChrome() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevContent = meta?.getAttribute('content') ?? null;
    const prevBodyBg = document.body.style.backgroundColor;
    meta?.setAttribute('content', AUTH_CHROME_COLOR);
    document.body.style.backgroundColor = AUTH_CHROME_COLOR;
    return () => {
      if (prevContent !== null) meta?.setAttribute('content', prevContent);
      document.body.style.backgroundColor = prevBodyBg;
    };
  }, []);
}

export function AuthShell({ title, subtitle, children }: {
  title: string; subtitle?: React.ReactNode; children: React.ReactNode;
}) {
  useAuthChrome();
  return (
    <div className="auth-stage">
      {/* El holograma (CB-23) va antes de la tarjeta en el DOM y detrás en
          z: es lo que el vidrio de `.auth-card` desenfoca. Se monta un
          cuadro después del formulario y, si falla, queda el degradado — el
          ingreso nunca depende de él. */}
      <AuthHologram />
      <div className="auth-card">
        <div className="auth-card__head">
          <div className="auth-card__mark"><Brandmark size={48} /></div>
          <h1 style={{ fontSize: 19 }}>{title}</h1>
          {subtitle && <p className="muted small mt-1">{subtitle}</p>}
        </div>
        <div className="auth-card__body">{children}</div>
        {/* `AuthShell` es compartido por login, invitación y restablecer —
            las tres son pantallas de entrada, así que el pie de
            Privacidad/Términos tiene sentido en las tres. */}
        <div className="auth-card__legal">
          <Link to="/privacidad">Privacidad</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terminos">Términos</Link>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { user, loading, login } = useSession();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (loading) return <Loading />;
  if (user) return <Navigate to={location.state?.from ?? homeFor(user.role)} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(location.state?.from ?? '/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('unknown', 'Algo salió mal', 500));
    } finally {
      setBusy(false);
    }
  }

  async function requestReset() {
    if (!email) {
      setError(new ApiError('email_required', 'Escribí tu correo primero', 400));
      return;
    }
    await api.post('/auth/request-reset', { email }).catch(() => undefined);
    // Se responde igual exista o no la cuenta: el formulario no sirve para
    // averiguar quién es cliente.
    setResetSent(true);
    setError(null);
  }

  return (
    <AuthShell title="Clean Blue" subtitle="Ingresá con tu correo y contraseña">
      {resetSent && (
        <Note tone="ok">
          Si ese correo tiene cuenta, le va a llegar un enlace para crear una contraseña nueva.
        </Note>
      )}
      {error && <Note tone="danger">{error.message}</Note>}

      <form onSubmit={submit} className="mt-2">
        <Field label="Correo">
          <input
            className="input"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>

        <Field label="Contraseña">
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            required
          />
        </Field>

        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      <div className="mt-3" style={{ textAlign: 'center' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void requestReset()}>
          Olvidé mi contraseña
        </button>
      </div>
    </AuthShell>
  );
}
